import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  LessonObjective,
  LessonBlock,
  LessonSlide,
  LessonPlanStatus,
  TechRatio,
  TechDetails,
  GoogleSlidesData,
  SlideThumbnailsResponse,
  GenerationStatus,
  GenerationStatusResponse,
  StartGenerationResponse,
} from './lesson-plan.models';

export interface CreateLessonPlanRequest {
  topic?: string;
  grade: number | null;
  tier1SubjectId?: string | null;
  tier2SubjectId?: string | null;
  usState?: string | null;
  duration?: string | null;
  instructionalModel?: string | null;
  techRatio?: TechRatio | null;
  techDetails?: TechDetails;
}

export interface LessonPlanResponse {
  id: string;
  status: LessonPlanStatus;
  generationStatus: GenerationStatus;
  generationError: string | null;
  topic: string;
  grade: number;
  tier1SubjectId?: string | null;
  tier2SubjectId?: string | null;
  usState?: string | null;
  duration: string | null;
  instructionalModel?: string | null;
  techRatio?: TechRatio | null;
  techDetails?: TechDetails;
  objectives: LessonObjective[];
  selectedObjectives: LessonObjective[];
  planStructure: LessonBlock[];
  slides: LessonSlide[];
  googleSlides: GoogleSlidesData | null;
  objectivesRegenerationCount: number;
  structureRegenerationCount: number;
  slidesRegenerationCount: number;
  // Export counters
  googleSlidesExportCount: number;
  teacherGuidePdfExportCount: number;
  slideDeckPptxExportCount: number;
  adminPlanGoogleDocsExportCount: number;
  adminPlanDocxExportCount: number;
  // Generation duration tracking (in milliseconds)
  objectivesGenerationDurationMs: number | null;
  structureGenerationDurationMs: number | null;
  slidesGenerationDurationMs: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateObjectivesResponse {
  lessonPlanId: string;
  objectives: LessonObjective[];
}

export interface GenerateStructureResponse {
  lessonPlanId: string;
  planStructure: LessonBlock[];
}

export interface GenerateSlidesResponse {
  lessonPlanId: string;
  slides: LessonSlide[];
  googleSlides: GoogleSlidesData | null;
}

export interface ExportToGoogleDocsResponse {
  documentUrl: string;
  documentId: string;
  title: string;
}

export interface UploadFilesResponse {
  lessonPlanId: string;
  uploadedFileIds: string[];
  vectorStoreId: string;
}

export interface UploadProgress {
  progress: number;
  completed: boolean;
  response?: UploadFilesResponse;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class LessonPlanApiService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/lesson-plans`;

  /**
   * Create a new lesson plan (Step 1)
   */
  createLessonPlan(request: CreateLessonPlanRequest): Observable<LessonPlanResponse> {
    return this.http
      .post<ApiResponse<LessonPlanResponse>>(this.apiUrl, request)
      .pipe(map((res) => res.data));
  }

  /**
   * Update lesson plan basic info (Steps 1-2 fields)
   * Used when user goes back to basics/settings and modifies input before regenerating
   */
  updateLessonPlan(
    lessonPlanId: string,
    request: CreateLessonPlanRequest
  ): Observable<LessonPlanResponse> {
    return this.http
      .put<ApiResponse<LessonPlanResponse>>(`${this.apiUrl}/${lessonPlanId}`, request)
      .pipe(map((res) => res.data));
  }

  /**
   * Upload files for a lesson plan
   */
  uploadFiles(lessonPlanId: string, files: File[]): Observable<UploadProgress> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return this.http
      .post<ApiResponse<UploadFilesResponse>>(`${this.apiUrl}/${lessonPlanId}/files`, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event: HttpEvent<ApiResponse<UploadFilesResponse>>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
              return { progress, completed: false };
            case HttpEventType.Response:
              return {
                progress: 100,
                completed: true,
                response: event.body?.data,
              };
            default:
              return { progress: 0, completed: false };
          }
        })
      );
  }

  /**
   * Start async objectives generation (Step 3 -> Step 4)
   * Returns immediately; client should poll for status
   */
  startObjectivesGeneration(lessonPlanId: string): Observable<StartGenerationResponse> {
    return this.http
      .post<ApiResponse<StartGenerationResponse>>(
        `${this.apiUrl}/${lessonPlanId}/generate-objectives`,
        {}
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Get generation status for polling
   * Lightweight endpoint that only returns status fields
   */
  getGenerationStatus(lessonPlanId: string): Observable<GenerationStatusResponse> {
    return this.http
      .get<ApiResponse<GenerationStatusResponse>>(
        `${this.apiUrl}/${lessonPlanId}/generation-status`
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Get a lesson plan by ID
   */
  getLessonPlan(lessonPlanId: string): Observable<LessonPlanResponse> {
    return this.http
      .get<ApiResponse<LessonPlanResponse>>(`${this.apiUrl}/${lessonPlanId}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Get all lesson plans for current user
   */
  getUserLessonPlans(): Observable<LessonPlanResponse[]> {
    return this.http
      .get<ApiResponse<LessonPlanResponse[]>>(this.apiUrl)
      .pipe(map((res) => res.data));
  }

  /**
   * Update objectives
   */
  updateObjectives(
    lessonPlanId: string,
    objectives: LessonObjective[]
  ): Observable<LessonPlanResponse> {
    return this.http
      .put<ApiResponse<LessonPlanResponse>>(`${this.apiUrl}/${lessonPlanId}/objectives`, {
        objectives,
      })
      .pipe(map((res) => res.data));
  }

  /**
   * Start async structure generation (Step 4 -> Step 5)
   * Returns immediately; client should poll for status
   */
  startStructureGeneration(
    lessonPlanId: string,
    selectedObjectiveIds: string[]
  ): Observable<StartGenerationResponse> {
    return this.http
      .post<ApiResponse<StartGenerationResponse>>(
        `${this.apiUrl}/${lessonPlanId}/generate-structure`,
        { selectedObjectiveIds }
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Start async slides generation (Step 5 -> Step 6)
   * Returns immediately; client should poll for status
   */
  startSlidesGeneration(lessonPlanId: string): Observable<StartGenerationResponse> {
    return this.http
      .post<ApiResponse<StartGenerationResponse>>(
        `${this.apiUrl}/${lessonPlanId}/generate-slides`,
        {}
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Update plan structure
   */
  updateStructure(
    lessonPlanId: string,
    planStructure: LessonBlock[]
  ): Observable<LessonPlanResponse> {
    return this.http
      .put<ApiResponse<LessonPlanResponse>>(`${this.apiUrl}/${lessonPlanId}/structure`, {
        planStructure,
      })
      .pipe(map((res) => res.data));
  }

  /**
   * Assign a lesson plan to the current user
   * Used after signup/login in the marketing funnel to claim a lesson plan
   */
  assignToUser(lessonPlanId: string): Observable<LessonPlanResponse> {
    return this.http
      .post<ApiResponse<LessonPlanResponse>>(`${this.apiUrl}/${lessonPlanId}/assign`, {})
      .pipe(map((res) => res.data));
  }

  /**
   * Update lesson plan status
   * Used to track user progress through the wizard steps
   */
  updateStatus(lessonPlanId: string, status: LessonPlanStatus): Observable<LessonPlanResponse> {
    return this.http
      .put<ApiResponse<LessonPlanResponse>>(`${this.apiUrl}/${lessonPlanId}/status`, { status })
      .pipe(map((res) => res.data));
  }

  /**
   * Get fresh thumbnails for the lesson plan's Google Slides presentation
   */
  getSlideThumbnails(lessonPlanId: string): Observable<SlideThumbnailsResponse> {
    return this.http
      .get<ApiResponse<SlideThumbnailsResponse>>(
        `${this.apiUrl}/${lessonPlanId}/slides/thumbnails`
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Export lesson plan as Teacher Guide PDF
   * Returns the PDF as a blob for download
   */
  exportToPdf(lessonPlanId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${lessonPlanId}/export/pdf`, {
      responseType: 'blob',
    });
  }

  /**
   * Export lesson plan slides as PPTX (PowerPoint format)
   * Returns the PPTX as a blob for download
   * This exports the Google Slides presentation directly to PPTX, preserving all styles
   */
  exportToPptx(lessonPlanId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${lessonPlanId}/export/pptx`, {
      responseType: 'blob',
    });
  }

  /**
   * Export lesson plan as Administrative Plan DOCX
   * Returns the DOCX as a blob for download
   * Formatted for school administrative review and approval
   */
  exportToDocx(lessonPlanId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${lessonPlanId}/export/docx`, {
      responseType: 'blob',
    });
  }

  /**
   * Export lesson plan as Administrative Plan Google Doc
   * Creates a Google Doc and returns the URL to open it
   */
  exportToGoogleDocs(lessonPlanId: string): Observable<ExportToGoogleDocsResponse> {
    return this.http
      .post<ApiResponse<ExportToGoogleDocsResponse>>(
        `${this.apiUrl}/${lessonPlanId}/export/google-docs`,
        {}
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Track Google Slides export (when user opens the presentation)
   * Used to increment the export counter
   */
  trackGoogleSlidesExport(lessonPlanId: string): Observable<void> {
    return this.http
      .post<ApiResponse<void>>(
        `${this.apiUrl}/${lessonPlanId}/export/google-slides/track`,
        {}
      )
      .pipe(map(() => undefined));
  }

}
